export type Tone = 'pink' | 'green' | 'amber' | 'slate' | 'rose' | 'blue';

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function timeAgo(value?: string | null): string {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const COMPANY_STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  INVITED: { label: 'Invited', tone: 'amber', hint: 'Waiting for the company to open the workspace' },
  INTAKE: { label: 'Intake', tone: 'pink', hint: 'Company profile and documents in progress' },
  SUBMITTED: { label: 'Submitted', tone: 'blue', hint: 'Documents submitted — awaiting rating' },
  RATED: { label: 'Rated', tone: 'green', hint: 'Rating published — pay the fee, then update docs if needed' },
  REMEDIATING: { label: 'Rated', tone: 'green', hint: 'Rating published — company can update documents and resubmit' },
  REANALYSIS: { label: 'Re-analysis', tone: 'blue', hint: 'Updated documents submitted — rating is being refreshed' },
  READY: { label: 'Ready', tone: 'green', hint: 'Ready summary agreed by company and analyst — start partner outreach' },
  OUTREACH: { label: 'Partner outreach', tone: 'green', hint: 'Partner meetings and discussions in progress' },
  FUNDED: { label: 'Funded', tone: 'green', hint: 'Funding secured' },
};

export const DOC_STATUS: Record<string, { label: string; tone: Tone }> = {
  UPLOADED: { label: 'Received', tone: 'slate' },
  VERIFYING: { label: 'Processing', tone: 'blue' },
  VERIFIED: { label: 'Verified', tone: 'green' },
  NEEDS_REVIEW: { label: 'Needs replacement', tone: 'amber' },
  REJECTED: { label: 'Needs replacement', tone: 'rose' },
  WRONG_ENTITY: { label: 'Wrong entity', tone: 'rose' },
  EXPIRED: { label: 'Expired', tone: 'rose' },
  NOT_APPLICABLE: { label: 'Not applicable', tone: 'slate' },
  ANALYST_REVIEW: { label: 'Analyst review required', tone: 'amber' },
  SUPERSEDED: { label: 'Replaced', tone: 'slate' },
};

export const SEVERITY: Record<string, { label: string; tone: Tone }> = {
  HIGH: { label: 'Major Concern', tone: 'rose' },
  MEDIUM: { label: 'Medium', tone: 'amber' },
  LOW: { label: 'Low', tone: 'slate' },
};

export const REMEDIATION_STATUS: Record<string, { label: string; tone: Tone }> = {
  OPEN: { label: 'Open', tone: 'pink' },
  SUBMITTED: { label: 'Addressed', tone: 'blue' },
  RESOLVED: { label: 'Resolved', tone: 'green' },
  SUPERSEDED: { label: 'Superseded', tone: 'slate' },
};

export const REPORT_STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  DRAFT: { label: 'Draft', tone: 'slate', hint: 'Only visible in Consult — edit, then publish to the customer' },
  PUBLISHED: { label: 'Published', tone: 'blue', hint: 'Visible to the customer (read only, after the rating fee is paid)' },
  APPROVED: { label: 'Ready agreed', tone: 'green', hint: 'Ready summary agreed and consent recorded — ready for partner outreach' },
  CHANGES_REQUESTED: { label: 'Changes requested', tone: 'rose', hint: 'Legacy: customer asked for revisions — generate a new draft' },
};

export const SUBMISSION_STATUS: Record<string, { label: string; tone: Tone }> = {
  SENT: { label: 'Engaged', tone: 'blue' },
  UNDER_REVIEW: { label: 'Under review', tone: 'blue' },
  INFO_REQUESTED: { label: 'Info requested', tone: 'amber' },
  OFFERED: { label: 'Offer received', tone: 'pink' },
  ACCEPTED: { label: 'Offer accepted', tone: 'green' },
  DISBURSED: { label: 'Disbursed', tone: 'green' },
  DECLINED: { label: 'Declined', tone: 'rose' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'slate' },
};

export const PAYMENT_STATUS: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: 'Due', tone: 'amber' },
  PAID: { label: 'Paid', tone: 'green' },
  FAILED: { label: 'Failed', tone: 'rose' },
  CANCELLED: { label: 'Cancelled', tone: 'slate' },
};

export const FEE_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'SGD', 'AED', 'AUD', 'CAD'] as const;

/** Amounts from the API are in minor units (paise / cents). */
export function formatMoney(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en', { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

export const DOC_CATEGORY: Record<string, { label: string; hint: string }> = {
  FINANCIAL: { label: 'Financial statements & banking', hint: 'Audited accounts, tax returns, bank statements, debt' },
  LEGAL: { label: 'Legal & statutory', hint: 'Incorporation, registrations, ownership, licences, contracts' },
  KYC: { label: 'Promoter / partner KYC', hint: 'Identity, net worth and consent for the people behind the business' },
  BUSINESS: { label: 'Business profile', hint: 'What you do, customers, operations' },
  OTHER: { label: 'Other documents', hint: 'Additional documents requested for your country' },
};

export const TRACK_TONE: Record<string, Tone> = { CORPORATE: 'blue', PARTNERSHIP: 'amber', PROPRIETORSHIP: 'green' };

/** Prefer the unique Micus display name over the real/legal company name. */
export function companyDisplayName(c: { logicalName?: string | null; name: string }): string {
  return c.logicalName || c.name;
}

export function companyTitle(c: { logicalName?: string | null; name: string; workspaceLabel?: string | null }): string {
  const base = companyDisplayName(c);
  return c.workspaceLabel ? `${base} · ${c.workspaceLabel}` : base;
}

export function flagEmoji(countryCode?: string | null): string {
  if (!countryCode || countryCode.length !== 2) return '🏳';
  return String.fromCodePoint(...[...countryCode.toUpperCase()].map((ch) => 0x1f1a5 + ch.charCodeAt(0)));
}

/** Ordered journey used for progress rails (Ready pack confirm is not a separate step). */
export const JOURNEY = [
  { key: 'INTAKE', label: 'Register' },
  { key: 'SUBMITTED', label: 'Submit docs' },
  { key: 'RATED', label: 'Rated' },
  { key: 'OUTREACH', label: 'Partners' },
  { key: 'FUNDED', label: 'Funded' },
] as const;

export function journeyIndex(status?: string | null): number {
  switch (status) {
    case 'INVITED':
    case 'INTAKE':
      return 0;
    case 'SUBMITTED':
      return 1;
    case 'RATED':
    case 'REMEDIATING':
    case 'REANALYSIS':
      return 2;
    case 'READY':
    case 'OUTREACH':
      return 3;
    case 'FUNDED':
      return 4;
    default:
      return -1;
  }
}

export function gradeTone(score?: number | null): Tone {
  if (score == null) return 'slate';
  if (score >= 75) return 'green';
  if (score >= 55) return 'amber';
  return 'rose';
}
